import dynamic from 'next/dynamic'
import { type FC } from 'react'

import { useDeviceDetectorContext } from '../../../device-detector'
import { ErrorBoundary } from '../../../error-boundary'
import { useGlobalConfigContext } from '../../../global-config'
import { MediServiceFurtherInformation } from '../../../medi-service'
import { TENANT } from '../../../tenant'
import { PdpRightAdBanner } from '../../ads/components/PdpRightAdBanner/PdpRightAdBanner'
import { productAccessorIsPrescriptionProduct } from '../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { ProductDescription } from '../ProductDescription'
import { ProductFaqAndReviews } from '../ProductFaqAndReviews'
import { ProductKeyFacts } from '../ProductKeyFacts'
import { ProductOverview } from '../ProductOverview'
import { useProductPageContext } from '../productPageContext'
import { ProductPharmacistAdvice } from '../ProductPharmacistAdvice'
import { ProductPrescriptionNotificationRequired } from '../ProductPrescriptionNotificationRequired'
import { ProductPrescriptionNotificationVideo } from '../ProductPrescriptionNotificationVideo'
import { DynamicProductRecommendationWidgetBuyTogether } from '../ProductRecommendationAreas/ProductRecommendationAreaBuyTogether'

const ProductAvailableRecommendationSliderArea = dynamic(async () =>
  import('../ProductAvailableRecommendationSliderArea').then(
    (mod) => mod.ProductAvailableRecommendationSliderArea,
  ),
)

/* eslint-disable react/no-multi-comp -- this has been done to allow for any logic to be added in the individual FCs. */
const RecommendationBuyTogether: FC = () => {
  return (
    <ErrorBoundary>
      <DynamicProductRecommendationWidgetBuyTogether />
    </ErrorBoundary>
  )
}

const BannerAndRecommendationBuyTogether: FC = () => {
  return (
    <>
      <PdpRightAdBanner />
      <RecommendationBuyTogether />
    </>
  )
}

const ProductAvailableRecommendationWidget: FC = () => {
  return (
    <ErrorBoundary>
      <ProductAvailableRecommendationSliderArea />
    </ErrorBoundary>
  )
}

const DefaultProductDetailsPosition: FC = () => {
  const { tenant } = useGlobalConfigContext()
  const { product } = useProductPageContext()

  /**
   * We show RX products in CH in order to increase the sales of prescription medicine in Switzerland through the use of Mediservice
   * We need to restrict several sections of the PDP and keep the page clear and informative regarding the product
   */
  const isPrescriptionProduct = productAccessorIsPrescriptionProduct(product)
  const isChTenant = tenant === TENANT.CH
  const isPrescriptionProductInChTenant = isPrescriptionProduct && isChTenant

  return (
    <>
      <ProductOverview />
      <ProductKeyFacts />
      <div className="mb-5 grid grid-cols-1 gap-5 tablet:grid-cols-2">
        <div className="flex flex-col gap-5">
          <ProductDescription className="flex-1" />
          {isPrescriptionProduct ? null : <ProductPharmacistAdvice />}
        </div>
        {!isPrescriptionProductInChTenant && (
          <ProductFaqAndReviews className="tablet:order-1 tablet:col-span-2" />
        )}
        <div className="flex w-full flex-1 flex-col gap-5">
          {isPrescriptionProductInChTenant ? (
            <MediServiceFurtherInformation
              className="pb-6 pt-0"
              displayInColumn
            />
          ) : (
            <>
              <ProductPrescriptionNotificationRequired />
              <ProductAvailableRecommendationWidget />
              <ProductPrescriptionNotificationVideo />
              <BannerAndRecommendationBuyTogether />
            </>
          )}
          {isPrescriptionProduct ? <ProductPharmacistAdvice /> : null}
        </div>
      </div>
    </>
  )
}
const PdpLowerProductDetailsWithoutProductAvailableRecommendationWidget: FC =
  () => {
    const { tenant } = useGlobalConfigContext()
    const { product } = useProductPageContext()

    const isPrescriptionProduct = productAccessorIsPrescriptionProduct(product)
    const isChTenant = tenant === TENANT.CH
    const isPrescriptionProductInChTenant = isPrescriptionProduct && isChTenant

    return (
      <div className="mb-5 grid grid-cols-1 gap-5 tablet:grid-cols-2">
        <ProductDescription className="flex-1" />
        <RecommendationBuyTogether />
        <ProductPharmacistAdvice />
        <ProductFaqAndReviews className="tablet:order-1 tablet:col-span-2" />
        <div className="flex w-full flex-1 flex-col gap-5">
          {!isPrescriptionProductInChTenant && (
            <>
              <ProductPrescriptionNotificationRequired />
              <ProductPrescriptionNotificationVideo />
              <PdpRightAdBanner />
            </>
          )}
        </div>
      </div>
    )
  }

export const ProductPageOverview: FC = () => {
  const { isMobile } = useDeviceDetectorContext()
  const { tenant } = useGlobalConfigContext()

  if (!isMobile) {
    return <DefaultProductDetailsPosition />
  }

  if (tenant === TENANT.BE) {
    return (
      <>
        <ProductOverview />
        <ProductKeyFacts />
        <div className="mb-5 grid grid-cols-1 gap-5 tablet:grid-cols-2">
          <ProductDescription className="flex-1" />
          <ProductAvailableRecommendationWidget />
          <RecommendationBuyTogether />
          <ProductFaqAndReviews className="tablet:order-1 tablet:col-span-2" />
          <div className="flex w-full flex-1 flex-col gap-5">
            <ProductPrescriptionNotificationRequired />
            <ProductPrescriptionNotificationVideo />
            <PdpRightAdBanner />
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <ProductOverview
        ProductAvailableRecommendationWidget={
          ProductAvailableRecommendationWidget
        }
      />
      <ProductKeyFacts />
      <PdpLowerProductDetailsWithoutProductAvailableRecommendationWidget />
    </>
  )
}
/* eslint-enable react/no-multi-comp */
